<template>
  <div class="scan-page">
    <div class="finish-scan">
      <button class="btn btn-green" @click="finishScan">Today's scan finished</button>
    </div>
    <div class="center-text">
      <input ref="inputRef" v-model="inputValue" type="text" placeholder="Search" class="track-num" />
      <button class="btn btn-green" @click="clearNumber">Clear</button>
      <button class="btn btn-green" @click="searchShipment">Submit</button>
    </div>
    <div v-if="shipments.length === 0 && search" class="not-found center-text">
      Are you sure you scanned the correct barcode?
    </div>
    <div v-if="received" class="received center-text">{{ received }}</div>
    <div v-if="error" class="receive-error center-text">{{ error }}</div>
    <ShipmentsScan v-if="shipments.length > 0" :shipments="shipments" />
  </div>
  <Confirm id="confirmScan" />
</template>

<script lang="ts">
import type { AxiosCollection } from '@/types/Components/Response'
import type { Shipment } from '@/types/Models/Shipment'
import { notify } from '@kyvg/vue3-notification'
import { computed, type ComputedRef, defineComponent, onMounted, onUnmounted, type Ref, ref, watch } from 'vue'
import { catchErrors, none, readableDateTime } from '@/services/Helpers'
import { openModal } from '@/services/Modal'
import { useVueNextModalStore } from '@/store/vueNextModal'
import moment from 'moment'
import Confirm from '@/views/Modals/Confirm.vue'
import ItemsProxy from '@/proxies/ItemsProxy'
import ShipmentProxy from '@/proxies/ShipmentProxy'
import ShipmentsScan from '@/views/Components/Shipments/ShipmentsScan.vue'
import ScanReportProxy from '@/proxies/ScanReportProxy'
import ScanDocumentProxy from '@/proxies/ScanDocumentProxy'

export default defineComponent({
  name: 'ScanPage',
  components: { Confirm, ShipmentsScan },
  setup() {
    const modalStore = useVueNextModalStore()
    const inputRef: Ref<HTMLInputElement | null> = ref(null)
    const inputValue: Ref<string> = ref('')
    const shipments: Ref<Shipment[]> = ref([])
    const received: Ref<string> = ref('')
    const search: Ref<boolean> = ref(false)
    const allow: Ref<boolean> = ref(true)
    const error: Ref<string> = ref('')
    const shipmentProxy = new ShipmentProxy()
    const itemsProxy = new ItemsProxy()
    let timeout: any

    // bugfix for multiple keypress handlers
    const isOpened: ComputedRef<boolean> = computed(() => modalStore.isModalVisible('confirmScan'))
    watch(isOpened, (newValue) => {
      setTimeout(() => {
        allow.value = !newValue
      }, 100)
    })
    //end bugfix

    const focusInput = () => {
      if (inputRef.value) {
        inputRef.value.focus()
        const length = inputRef.value.value.length
        inputRef.value.setSelectionRange(length, length)
      }
    }
    const handleKeyPress = (event: KeyboardEvent) => {
      if (!allow.value) return
      if (event.key === 'Enter') {
        focusInput()
        receiveFirst()
      }
    }
    const receiveFirst = () => {
      search.value = false
      error.value = ''
      const firstShipment = shipments.value[0]
      if (firstShipment) {
        const receive = () => {
          itemsProxy
            .receive(firstShipment.id)
            .then((response) => {
              shipments.value = shipments.value.filter((shipment) => shipment.id !== firstShipment.id)
              received.value = response?.message || 'Shipment received in facility'
              setTimeout(() => {
                received.value = ''
              }, 5000)
            })
            .catch((e) => {
              error.value = e && typeof e === 'object' && 'message' in e ? String(e.message) : JSON.stringify(e)
              setTimeout(() => {
                error.value = ''
              }, 5000)
            })
        }

        if (firstShipment.receivedInFacilityAt) {
          const minutesAgo = moment().diff(moment(firstShipment.receivedInFacilityAt), 'minutes')
          openModal('confirmScan', {
            title: 'Confirm scan',
            message: `This label was scanned already ${minutesAgo} minutes ago. At ${readableDateTime(firstShipment.receivedInFacilityAt)}. Are you sure you want to mark this shipment scanned?`,
            onConfirm: receive,
          })
        } else {
          receive()
        }
      }
    }
    const searchShipment = () => {
      clearTimeout(timeout)
      focusInput()
      if (inputValue.value.length > 8) {
        search.value = false
        timeout = setTimeout(() => {
          /**
           *  'Följesedlar-Malmö.Enstycksförsändelser'
           *  'Följesedlar-Malmö.UtrikesBrevsändning'
           *  'Följesedlar-Malmö.OsorteradSändningEkonomibrev'
           */
          if (inputValue.value.includes('ljesedlar')) {
            const qrcode = inputValue.value.trim()
            openModal('confirmWrap', {
              title: 'Confirm Notification',
              message: 'We’ve detected new shipment material. Notify recipient?',
              onConfirm: () => {
                new ScanDocumentProxy()
                  .create({ qrcode })
                  .then(() => {
                    notify({ group: 'success', text: 'Notification sent' })
                  })
                  .catch(catchErrors)
                focusInput()
              },
            })

            clearNumber()
            shipments.value = []
            return
          }
          shipmentProxy
            .getShipmentsByTrackingNumber(inputValue.value)
            .then((response: AxiosCollection<Shipment>) => {
              shipments.value = response.data
              clearNumber()
              search.value = true
            })
            .catch(() => {
              search.value = true
            })
        }, 500)
      }
    }
    const clearNumber = () => {
      inputValue.value = ''
      focusInput()
    }
    const finishScan = () => {
      new ScanReportProxy()
        .create({})
        .then(() => {
          notify({ group: 'success', text: 'Report sent' })
        })
        .catch(none)
    }

    watch(inputValue, searchShipment)

    onMounted(() => {
      const interval = setInterval(focusInput, 3000)
      focusInput()
      onUnmounted(() => {
        clearInterval(interval)
      })
      window.addEventListener('keydown', handleKeyPress)
    })

    return {
      searchShipment,
      clearNumber,
      finishScan,
      inputValue,
      shipments,
      received,
      inputRef,
      search,
      error,
    }
  },
})
</script>
